function findBestTriplet(bets) {
  if (bets.length < 3) return bets;
  const sorted = [...bets].sort((a, b) => a.odds - b.odds);
  const inverses = sorted.map((bet) => 1 / bet.odds);
  const inverseRange = inverses.at(-1) - inverses[0];
  const targets = [
    inverses[0],
    inverses[0] + inverseRange / 2,
    inverses.at(-1),
  ];
  const usedIndices = new Set();

  return targets.map((target) => {
    let selectedIndex = -1;
    let smallestDifference = Infinity;
    inverses.forEach((inverse, index) => {
      const difference = Math.abs(inverse - target);
      if (!usedIndices.has(index) && difference < smallestDifference) {
        selectedIndex = index;
        smallestDifference = difference;
      }
    });
    usedIndices.add(selectedIndex);
    return sorted[selectedIndex];
  });
}

function sortAndPadTriplet(triplet, direction) {
  const sorted = triplet
    .filter(Boolean)
    .sort((a, b) => a.odds - b.odds)
    .map((bet) => ({
      title: `${direction === "OVER" ? "↑" : "↓"}${bet.strike}`,
      odds: bet.odds.toFixed(2),
      betId: bet.betId,
      disabled: false,
    }));

  while (sorted.length < 3) {
    sorted.push({ title: "N/A", odds: "-", betId: "N/A", disabled: true });
  }
  return sorted;
}

function toOddsButton(bet, direction) {
  const odds = Number(bet?.odds);
  const hasOdds = bet?.odds !== null && Number.isFinite(odds);
  return bet
    ? {
        title: `${direction === "OVER" ? "↑" : "↓"}${bet.strike}`,
        odds: hasOdds ? odds : null,
        betId: bet.betId,
        disabled: !hasOdds,
      }
    : {
        title: direction === "OVER" ? "Above N/A" : "Below N/A",
        odds: null,
        betId: "N/A",
        disabled: true,
      };
}

function getPromotedStrikes(group) {
  const promoted = group.filter((bet) => bet.status === "promoted");
  const strikes = [...new Set(promoted.map((bet) => Number(bet.strike)))].sort(
    (a, b) => a - b,
  );
  return strikes.map((strike) => ({
    strike,
    over: toOddsButton(
      promoted.find(
        (bet) => Number(bet.strike) === strike && bet.direction === "OVER",
      ),
      "OVER",
    ),
    under: toOddsButton(
      promoted.find(
        (bet) => Number(bet.strike) === strike && bet.direction === "UNDER",
      ),
      "UNDER",
    ),
  }));
}

export function processBets(inputData) {
  const groupedByCurrency = {};
  for (const entry of inputData) {
    const odds = parseFloat(entry.last_price);
    const currency = entry.currency;
    if (!groupedByCurrency[currency]) groupedByCurrency[currency] = {};
    const groupKey = `${currency}-${new Date(entry.maturity_date) / 1000 / (60 * 5)}`;
    if (!groupedByCurrency[currency][groupKey])
      groupedByCurrency[currency][groupKey] = [];
    groupedByCurrency[currency][groupKey].push({
      betId: entry.name,
      strike: entry.strike,
      direction: entry.direction,
      odds: Number.isFinite(odds) ? odds : null,
      status: entry.status,
    });
  }

  const currencyPriority = ["BTC", "ETH", "SOL", "DOGE"];
  const sortedCurrencies = Object.keys(groupedByCurrency).sort((a, b) => {
    const firstPriority = currencyPriority.indexOf(a);
    const secondPriority = currencyPriority.indexOf(b);
    if (firstPriority === -1 && secondPriority === -1)
      return a.localeCompare(b);
    if (firstPriority === -1) return 1;
    if (secondPriority === -1) return -1;
    return firstPriority - secondPriority;
  });

  return sortedCurrencies.map((currency) => {
    const products = Object.entries(groupedByCurrency[currency])
      .map(([key, group]) => {
        const featured = group.filter(
          (bet) => Number.isFinite(bet.odds) && bet.odds <= 5,
        );
        const promotedStrikes = getPromotedStrikes(group);
        if (featured.length === 0 && promotedStrikes.length === 0) return null;
        const overTriplet = findBestTriplet(
          featured.filter((bet) => bet.direction === "OVER"),
        );
        const underTriplet = findBestTriplet(
          featured.filter((bet) => bet.direction === "UNDER"),
        );
        const overs = sortAndPadTriplet(overTriplet, "OVER");
        const unders = sortAndPadTriplet(underTriplet, "UNDER");
        const [, period] = key.split("-");
        return {
          title: key,
          starred: true,
          info1: "...",
          info2: "...",
          disabled: false,
          date: Number(period) * 60 * 5,
          promotedStrikes,
          odds: {
            L1: overs[0],
            L2: overs[1],
            L3: overs[2],
            S1: unders[0],
            S2: unders[1],
            S3: unders[2],
          },
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.date - b.date);
    return { currency, products };
  });
}
